import React, { useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Circle, CircleMarker, Polygon, Polyline, Tooltip, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { UsersIcon, TargetIcon, MapPinIcon, FilterIcon, CheckIcon, SearchIcon, Trash2Icon, BookOpenIcon, CalendarIcon, HomeIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { AGE_BRACKETS, DEPARTMENTS, WINDSOR_ADDRESSES, WINDSOR_CENTER, RADIUS_OPTIONS, RESIDENCE_OPTIONS, SECTORS, type AddressEntry, type Coord } from '../../data/communications';
import { householdsInSector, householdsNear, type Household } from '../../utils/households';
import type { AudienceState } from '../../types/composer';
import type { AudienceKind } from '../../types/notice';
const mapMarkerIcon = L.divIcon({
  className: '',
  html: `<div style="width:20px;height:20px;border-radius:9999px;background:#4F46E5;border:3px solid white;box-shadow:0 0 0 3px rgba(79,70,229,0.3);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10]
});

/** Captures map clicks while the user traces a custom polygon. */
function DrawLayer({
  active,
  points,
  onAddPoint
}: {
  active: boolean;
  points: Coord[];
  onAddPoint: (c: Coord) => void;
}) {
  useMapEvents({
    click(e) {
      if (active) onAddPoint([e.latlng.lat, e.latlng.lng]);
    }
  });
  if (points.length === 0) return null;
  return <>
      {points.length >= 3 && <Polygon positions={points} pathOptions={{
      color: '#4F46E5',
      fillColor: '#4F46E5',
      fillOpacity: 0.15
    }} data-id="element-3749" data-mp="6srsnf23d8ti" />}
      {points.length === 2 && <Polyline positions={points} pathOptions={{
      color: '#4F46E5'
    }} data-id="element-3750" data-mp="blcnmjwqhrym" />}
      {points.map((p, i) => <Circle key={i} center={p} radius={12} pathOptions={{
      color: '#4F46E5',
      fillColor: '#fff',
      fillOpacity: 1
    }} data-id="element-3751" data-mp="3f8lbw1nt4ye9" data-mpi={i} />)}
    </>;
}
interface AudienceSelectorProps {
  audience: AudienceState;
  onChange: (patch: Partial<AudienceState>) => void;
  audienceTotal: number;
  showSuggestions: boolean;
  onShowSuggestions: (v: boolean) => void;
}
export function AudienceSelector({
  audience,
  onChange,
  audienceTotal,
  showSuggestions,
  onShowSuggestions
}: AudienceSelectorProps) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const addressBoxRef = useRef<HTMLDivElement>(null);

  // Clicking (or tabbing) anywhere outside the address field closes its list.
  useEffect(() => {
    if (!showSuggestions) return;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!addressBoxRef.current?.contains(event.target as Node)) {
        onShowSuggestions(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onShowSuggestions(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showSuggestions, onShowSuggestions]);
  const {
    zone,
    selectedSectors,
    locationSearch,
    locationRadius,
    addrCoords,
    drawnPoints,
    isDrawing,
    selectedDepartments,
    selectedAges,
    residence
  } = audience;
  const addressMatches = locationSearch ? WINDSOR_ADDRESSES.filter(a => a.label.toLowerCase().includes(locationSearch.toLowerCase())) : WINDSOR_ADDRESSES;
  const selectAddress = (entry: AddressEntry) => {
    onChange({
      locationSearch: entry.label,
      addrCoords: entry.coord
    });
    onShowSuggestions(false);
  };

  /** Adding/removing a district — shared by the checkbox list and the map. */
  const toggleSector = (id: string) => onChange({
    selectedSectors: selectedSectors.includes(id) ? selectedSectors.filter(s => s !== id) : [...selectedSectors, id]
  });

  /** Dwellings inside every selected district, each tagged with its sector. */
  const sectorHouseholds = useMemo(() => selectedSectors.flatMap(id => {
    const sector = SECTORS.find(s => s.id === id);
    if (!sector) return [];
    return householdsInSector(id).map(household => ({
      sector,
      household
    }));
  }), [selectedSectors]);

  /** Dwellings inside the radius drawn around the searched address. */
  const nearbyHouseholds: Household[] = useMemo(() => addrCoords ? householdsNear(addrCoords, Number(locationRadius)) : [], [addrCoords, locationRadius]);
  const zones: {
    id: AudienceKind;
    icon: typeof UsersIcon;
    label: string;
  }[] = [{
    id: 'org',
    icon: UsersIcon,
    label: t("Toute l'organisation", 'Entire organization')
  }, {
    id: 'sector',
    icon: TargetIcon,
    label: t('Secteur spécifique', 'Specific district')
  }, {
    id: 'location',
    icon: MapPinIcon,
    label: t("Autour d'un lieu", 'Around a location')
  }, {
    id: 'filter',
    icon: FilterIcon,
    label: t('Par filtre', 'By filter')
  }];
  return <section className="space-y-6" data-id="element-3752" data-mp="2t1dg5lgd2gxt">
      <h2 className="text-xl font-semibold text-slate-900" data-id="element-3753" data-mp="2x5l59jqqdywk">
        {t('Audience', 'Audience')}
      </h2>
      <div className="bg-white rounded-xl border-[0.5px] border-slate-200 p-6 space-y-6" data-id="element-3754" data-mp="323cto3s632w0">
        <div className="flex flex-wrap gap-3" data-id="element-3755" data-mp="36ubftjzu6v76">
          {zones.map(z => {
          const Icon = z.icon;
          const active = zone === z.id;
          return <button key={z.id} onClick={() => onChange({
            zone: z.id
          })} className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors border ${active ? 'bg-indigo-50 border-[#4F46E5] text-[#4F46E5]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-3756" data-mp="3gt0yhezk4joh">
                <Icon className="w-4 h-4" data-id="element-3757" data-mp="1uj7qqpxp8b5e" /> {z.label}
              </button>;
        })}
        </div>

        <AnimatePresence mode="wait" data-id="element-3758" data-mp="1ppwluucg64g4">
          {zone === 'sector' && <motion.div key="sector" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-3759" data-mp="24fauu1c42juz">
              <div className="pt-2" data-id="element-3760" data-mp="29gi3s3pzpp38">
                <div className="flex items-center justify-between mb-3" data-id="element-3761" data-mp="29dcbygpz893t">
                  <label className="block text-sm font-medium text-slate-700" data-id="element-3762" data-mp="j3j6w7fqrs8t">
                    {t('Sélectionnez les secteurs à contacter', 'Select the districts to contact')}
                  </label>
                  <button type="button" onClick={() => onChange({
                selectedSectors: selectedSectors.length === SECTORS.length ? [] : SECTORS.map(s => s.id)
              })} className="text-xs font-semibold text-[#4F46E5] hover:text-[#4338CA]" data-id="element-3763" data-mp="8zc6w8tlf1wz">
                    {selectedSectors.length === SECTORS.length ? t('Tout désélectionner', 'Deselect all') : t('Tout sélectionner', 'Select all')}
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3764" data-mp="16o5tkcaeys5t">
                  {SECTORS.map(s => {
                const checked = selectedSectors.includes(s.id);
                return <label key={s.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-[#4F46E5] bg-indigo-50/30' : 'border-slate-200 hover:bg-slate-50'}`} data-id="element-3765" data-mp="2k5la5x4netaq">
                        <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-[#4F46E5] border-[#4F46E5]' : 'border-slate-300 bg-white'}`} data-id="element-3766" data-mp="21h6jjay8thag">
                          {checked && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-3767" data-mp="3lup198tm51wb" />}
                        </div>
                        <div className="min-w-0 flex-1" data-id="element-3768" data-mp="3hv6iojp9l2wn">
                          <span className="block text-sm font-medium text-slate-700 truncate" data-id="element-3769" data-mp="13mdnykja0z5i">
                            {s.label[lang]}
                          </span>
                          <span className="block text-xs text-slate-400" data-id="element-3770" data-mp="18jm64l4fd36r">
                            {s.population.toLocaleString(locale)}{' '}
                            {t('résidents', 'residents')}
                          </span>
                        </div>
                        <input type="checkbox" className="hidden" checked={checked} onChange={() => toggleSector(s.id)} data-id="element-3771" data-mp="kojigghcx5rv" />
                      </label>;
              })}
                </div>

                <p className="mt-3 text-sm text-slate-500" data-id="element-3772" data-mp="yl6gynoyk3zw">
                  {selectedSectors.length}{' '}
                  {t('secteur(s)', 'district(s)')} ·{' '}
                  <span className="font-semibold text-slate-700" data-id="element-3773" data-mp="2a14ub3r65c6x">
                    {audienceTotal.toLocaleString(locale)}
                  </span>{' '}
                  {t('résidents ciblés', 'residents targeted')}
                </p>

                {/* Map preview — clicking a district toggles it in the list */}
                <div className="mt-4 h-64 rounded-xl overflow-hidden border border-slate-200 relative" data-id="element-3774" data-mp="1g5lvlqo86bor">
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[400] pointer-events-none" data-id="element-3775" data-mp="1l5zuoj7umban">
                    <span className="bg-white/90 backdrop-blur-sm px-4 py-1.5 rounded-full shadow-sm border border-slate-200 text-xs font-medium text-slate-500" data-id="element-3776" data-mp="ni97vd6bvxtv">
                      {t('Cliquez un secteur sur la carte pour le sélectionner', 'Click a district on the map to select it')}
                    </span>
                  </div>
                  <MapContainer center={WINDSOR_CENTER} zoom={13} scrollWheelZoom={false} style={{
                height: '100%',
                width: '100%'
              }} data-id="element-3777" data-mp="2cv2zq17k7poc">
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-3778" data-mp="3tba691ung536" />
                    {SECTORS.map(s => {
                  const selected = selectedSectors.includes(s.id);
                  return <Polygon key={s.id} positions={s.polygon} eventHandlers={{
                    click: () => toggleSector(s.id)
                  }} pathOptions={{
                    color: selected ? s.color : '#94A3B8',
                    weight: selected ? 2 : 1,
                    fillColor: selected ? s.color : '#CBD5E1',
                    fillOpacity: selected ? 0.18 : 0.05,
                    dashArray: selected ? undefined : '4 4',
                    className: 'cursor-pointer'
                  }} data-id="element-3779" data-mp="39n1dapla7v2m">
                          <Tooltip direction="center" permanent={selected} data-id="element-3780" data-mp="18zexw5qay641">
                            {s.label[lang]}
                          </Tooltip>
                        </Polygon>;
                })}

                    {/* One dot per dwelling, so the exact addresses covered
                        by a selected district can be verified on hover. */}
                    {sectorHouseholds.map(({
                  sector,
                  household
                }) => <CircleMarker key={household.id} center={household.coord} radius={4} pathOptions={{
                  color: '#ffffff',
                  weight: 1.5,
                  fillColor: sector.color,
                  fillOpacity: 1
                }} data-id="element-3781" data-mp="1rpegc9rn0myh">
                        <Tooltip direction="top" offset={[0, -6]} opacity={1} data-id="element-3782" data-mp="ds5yjesb6l1g">
                          <span className="font-semibold" data-id="element-3783" data-mp="8qyrjhxzppvu">
                            {household.address}
                          </span>
                          <br data-id="element-3784" data-mp="hs6e52r592a3" />
                          <span className="text-slate-500" data-id="element-3785" data-mp="ht7yxyxyp350">
                            {sector.label[lang]}
                            {' · '}
                            {household.accounts}{' '}
                            {household.accounts > 1 ? t('comptes bciti+', 'bciti+ accounts') : t('compte bciti+', 'bciti+ account')}
                          </span>
                        </Tooltip>
                      </CircleMarker>)}
                  </MapContainer>

                  {sectorHouseholds.length > 0 && <div className="absolute bottom-3 left-3 z-[400] px-2.5 py-1.5 rounded-lg bg-white/95 border border-slate-200 shadow-sm text-xs text-slate-600" data-id="element-3786" data-mp="32hdx4k93kbve">
                      {t('Survolez un point pour voir l’adresse', 'Hover a dot to see the address')}
                    </div>}
                </div>
              </div>
            </motion.div>}

          {zone === 'location' && <motion.div key="location" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-3787" data-mp="2p6k23210tut">
              <div className="pt-2 space-y-4" data-id="element-3788" data-mp="hmqjkug5lwtl">
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-3789" data-mp="2efsbx10fzg3q">
                  <div className="flex-1" data-id="element-3790" data-mp="2ehd9z0lyth6c">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3791" data-mp="2je3nphaas0ol">
                      {t('Adresse centrale', 'Centre address')}
                    </label>
                    <div className="relative" ref={addressBoxRef} data-id="element-3792" data-mp="vsshrooxskez">
                      <input type="text" value={locationSearch} onChange={e => {
                    onChange({
                      locationSearch: e.target.value
                    });
                    onShowSuggestions(true);
                  }} onFocus={() => onShowSuggestions(true)} placeholder={t('Rechercher une adresse à Windsor…', 'Search for an address in Windsor…')} aria-label={t('Rechercher une adresse', 'Search for an address')} className="w-full border border-slate-200 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-3793" data-mp="vrhdzovhrcnv" />
                      <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3794" data-mp="3rpgmgfhpqdtj" />
                      {showSuggestions && addressMatches.length > 0 && <ul className="absolute z-[1100] mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto py-1" data-id="element-3795" data-mp="3ipk1anuj7x38">
                          {addressMatches.map(a => <li key={a.label} data-id="element-3796" data-mp="38yyu4c0slk8p">
                              <button type="button" onClick={() => selectAddress(a)} className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-slate-700 hover:bg-indigo-50" data-id="element-3797" data-mp="38y5x42rmy93r">
                                <MapPinIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-3798" data-mp="33y1bjybveo6o" />
                                {a.label}
                              </button>
                            </li>)}
                        </ul>}
                    </div>
                  </div>
                  <div className="w-full sm:w-48" data-id="element-3799" data-mp="26fmdykct3yro">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-3800" data-mp="26gxd26vb4icg">
                      {t('Rayon', 'Radius')}
                    </label>
                    <div className="relative" data-id="element-3801" data-mp="2gbz81jc9kemy">
                      <select value={locationRadius} onChange={e => onChange({
                    locationRadius: e.target.value
                  })} disabled={drawnPoints.length >= 3} className="w-full border border-slate-200 rounded-lg pl-3 pr-10 py-2 text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5] disabled:bg-slate-50 disabled:text-slate-400" data-id="element-3802" data-mp="2gfd7aipms7q6">
                        {RADIUS_OPTIONS.map(r => <option key={r.value} value={r.value} data-id="element-3803" data-mp="392pjbyy60x58">
                            {r.label[lang]}
                          </option>)}
                      </select>
                      <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-3804" data-mp="o99q41yccz14" />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between flex-wrap gap-2" data-id="element-3805" data-mp="x9prn5vixw3c">
                  <span className="text-sm font-medium text-slate-700" data-id="element-3806" data-mp="ljiq6rkkhj9">
                    {t('Ou dessiner une zone personnalisée', 'Or draw a custom area')}
                  </span>
                  <div className="flex items-center gap-2" data-id="element-3807" data-mp="9jbzzggupz9a">
                    {drawnPoints.length > 0 && <button type="button" onClick={() => onChange({
                  drawnPoints: [],
                  isDrawing: false
                })} className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors" data-id="element-3808" data-mp="3dzmtmtzswpkv">
                        <Trash2Icon className="w-4 h-4" data-id="element-3809" data-mp="35jxm8ogk4c5m" /> {t('Effacer', 'Clear')}
                      </button>}
                    <button type="button" onClick={() => isDrawing ? onChange({
                  isDrawing: false
                }) : onChange({
                  drawnPoints: [],
                  isDrawing: true
                })} className={`text-sm font-medium px-3 py-1.5 rounded-md transition-colors ${isDrawing ? 'bg-indigo-100 text-[#4F46E5]' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-3810" data-mp="31j5anok7pv66">
                      {isDrawing ? t('Terminer le tracé', 'Finish drawing') : t('Dessiner sur la carte', 'Draw on the map')}
                    </button>
                  </div>
                </div>

                <div className="h-64 rounded-xl overflow-hidden border border-slate-200 mt-2 relative" data-id="element-3811" data-mp="1xmw0fzfo4dve">
                  {isDrawing && <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[400] bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-slate-200 text-xs font-medium text-[#4F46E5]" data-id="element-3812" data-mp="1soufta2lg6ht">
                      {t('Cliquez sur la carte pour ajouter des points', 'Click the map to add points')}
                    </div>}
                  <MapContainer center={addrCoords ?? WINDSOR_CENTER} zoom={13} scrollWheelZoom={false} style={{
                height: '100%',
                width: '100%'
              }} data-id="element-3813" data-mp="14xf6uygbaqxl">
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-3814" data-mp="vx0ly079fdum" />
                    {addrCoords && drawnPoints.length < 3 && !isDrawing && <>
                        <Marker position={addrCoords} icon={mapMarkerIcon} data-id="element-3815" data-mp="3ljd8bc6qra9i" />
                        <Circle center={addrCoords} radius={parseInt(locationRadius)} pathOptions={{
                    color: '#4F46E5',
                    fillColor: '#4F46E5',
                    fillOpacity: 0.12
                  }} data-id="element-3816" data-mp="3lh95yyohqjbc" />
                      </>}
                    {/* Dwellings caught by the radius, address on hover. */}
                    {drawnPoints.length < 3 && !isDrawing && nearbyHouseholds.map(household => <CircleMarker key={household.id} center={household.coord} radius={4} pathOptions={{
                  color: '#ffffff',
                  weight: 1.5,
                  fillColor: '#4F46E5',
                  fillOpacity: 1
                }} data-id="element-3817" data-mp="222pccqzv1i8p">
                          <Tooltip direction="top" offset={[0, -6]} opacity={1} data-id="element-3818" data-mp="1j6ps62d4jizo">
                            <span className="font-semibold" data-id="element-3819" data-mp="1f546brim3udc">
                              {household.address}
                            </span>
                            <br data-id="element-3820" data-mp="1o3g8mqsucglm" />
                            <span className="text-slate-500" data-id="element-3821" data-mp="1o37w500mrday">
                              {household.accounts}{' '}
                              {household.accounts > 1 ? t('comptes bciti+', 'bciti+ accounts') : t('compte bciti+', 'bciti+ account')}
                            </span>
                          </Tooltip>
                        </CircleMarker>)}
                    <DrawLayer active={isDrawing} points={drawnPoints} onAddPoint={c => onChange({
                  drawnPoints: [...drawnPoints, c]
                })} data-id="element-3822" data-mp="cap1f2qxug4v" />
                  </MapContainer>

                  {nearbyHouseholds.length > 0 && drawnPoints.length < 3 && !isDrawing && <div className="absolute bottom-3 left-3 z-[400] px-2.5 py-1.5 rounded-lg bg-white/95 border border-slate-200 shadow-sm text-xs text-slate-600" data-id="element-3823" data-mp="v9kta7fbqifj">
                        {t(`${nearbyHouseholds.length} adresses · survolez un point`, `${nearbyHouseholds.length} addresses · hover a dot`)}
                      </div>}
                </div>

                {audienceTotal > 0 && <p className="text-sm text-slate-500" data-id="element-3824" data-mp="3v9f81ov1s3c6">
                    {drawnPoints.length >= 3 ? t('Zone personnalisée', 'Custom area') : t("Zone autour de l'adresse", 'Area around the address')}{' '}
                    · {t('environ', 'about')}{' '}
                    <span className="font-semibold text-slate-700" data-id="element-3825" data-mp="mtm1o2ufk88u">
                      {audienceTotal.toLocaleString(locale)}
                    </span>{' '}
                    {t('résidents', 'residents')}
                  </p>}
              </div>
            </motion.div>}

          {zone === 'filter' && <motion.div key="filter" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-3826" data-mp="915y90xma87e">
              <div className="pt-2 space-y-6" data-id="element-3827" data-mp="193ccvgjhgyvb">
                {/* Departments */}
                <div data-id="element-3828" data-mp="1e0cad329otat">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3829" data-mp="1e1nn3sdoxbe2">
                    <BookOpenIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-3830" data-mp="1izp2o3bwqsks" />
                    <label className="block text-sm font-medium text-slate-700" data-id="element-3831" data-mp="1n1tx34a6jt8h">
                      {t('Département de la ville', 'City department')}
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-3832" data-mp="1wzzvvd0owj94">
                    {DEPARTMENTS.map(d => {
                  const active = selectedDepartments.includes(d.id);
                  return <button key={d.id} type="button" onClick={() => onChange({
                    selectedDepartments: active ? selectedDepartments.filter(x => x !== d.id) : [...selectedDepartments, d.id]
                  })} className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-indigo-50 border-[#4F46E5] text-[#4F46E5]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-3833" data-mp="26pjh2pbvkp75">
                          {active && <CheckIcon className="w-3.5 h-3.5" data-id="element-3834" data-mp="uf8kpsbv2u4i" />}
                          {d.label[lang]}
                        </button>;
                })}
                  </div>
                  <p className="mt-2 text-xs text-slate-400" data-id="element-3835" data-mp="13bq057r9de62">
                    {t('Laissez vide pour cibler tous les départements.', 'Leave empty to target every department.')}
                  </p>
                </div>

                {/* Age */}
                <div data-id="element-3836" data-mp="1pm80pejbf6ad">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3837" data-mp="1pmhi4dqao54g">
                    <CalendarIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-3838" data-mp="1po2hcgnk0c8n" />
                    <label className="block text-sm font-medium text-slate-700" data-id="element-3839" data-mp="1buktwkrqyfdc">
                      {t("Tranche d'âge", 'Age bracket')}
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-3840" data-mp="11v2d2d74v9pi">
                    {AGE_BRACKETS.map(a => {
                  const active = selectedAges.includes(a.id);
                  return <button key={a.id} type="button" onClick={() => onChange({
                    selectedAges: active ? selectedAges.filter(x => x !== a.id) : [...selectedAges, a.id]
                  })} className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-indigo-50 border-[#4F46E5] text-[#4F46E5]' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-3841" data-mp="sxjqh4ducnbi">
                          {active && <CheckIcon className="w-3.5 h-3.5" data-id="element-3842" data-mp="rcx8hg0ixz5h" />}
                          {a.label[lang]}
                        </button>;
                })}
                  </div>
                  <p className="mt-2 text-xs text-slate-400" data-id="element-3843" data-mp="hepw7ah6k59t">
                    {t('Laissez vide pour cibler tous les âges.', 'Leave empty to target every age.')}
                  </p>
                </div>

                {/* Residence */}
                <div data-id="element-3844" data-mp="2ka12u74yziux">
                  <div className="flex items-center gap-2 mb-3" data-id="element-3845" data-mp="2k8q10cv34l6g">
                    <HomeIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-3846" data-mp="2oal1xg93swjp" />
                    <label className="block text-sm font-medium text-slate-700" data-id="element-3847" data-mp="2o90a3ye6brpj">
                      {t('Type de résidence', 'Residence type')}
                    </label>
                  </div>
                  <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-id="element-3848" data-mp="2y30hk6wxh91f">
                    {RESIDENCE_OPTIONS.map(r => {
                  const active = residence === r.value;
                  return <button key={r.value} type="button" onClick={() => onChange({
                    residence: r.value
                  })} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${active ? 'bg-white text-[#4F46E5] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3849" data-mp="2ab25hkpnn9n8">
                          {r.label[lang]}
                        </button>;
                })}
                  </div>
                </div>

                {/* Summary */}
                <div className="rounded-lg bg-indigo-50/60 border border-indigo-100 px-4 py-3" data-id="element-3850" data-mp="1r8cj8c1r78kb">
                  <p className="text-sm text-slate-600" data-id="element-3851" data-mp="1cb250uxq7okw">
                    {t('Audience filtrée', 'Filtered audience')} ·{' '}
                    {t('environ', 'about')}{' '}
                    <span className="font-semibold text-[#4F46E5]" data-id="element-3852" data-mp="13byl5oqqj55c">
                      {audienceTotal.toLocaleString(locale)}
                    </span>{' '}
                    {t('citoyens', 'citizens')}
                  </p>
                  <p className="mt-1 text-xs text-slate-400" data-id="element-3853" data-mp="2xqrgrnrmbvxa">
                    {selectedDepartments.length > 0 ? `${selectedDepartments.length} ${t('département(s)', 'department(s)')}` : t('Tous les départements', 'All departments')}{' '}
                    ·{' '}
                    {selectedAges.length > 0 ? AGE_BRACKETS.filter(a => selectedAges.includes(a.id)).map(a => a.label[lang]).join(', ') : t('Tous les âges', 'All ages')}{' '}
                    ·{' '}
                    {RESIDENCE_OPTIONS.find(r => r.value === residence)?.label[lang] ?? t('Tous', 'All')}
                  </p>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}